import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { signInWithGoogle } from '../firebase/authService';

// Super Admin Emails - Always have admin role
const SUPER_ADMIN_EMAILS = [
  'fayedruet@gmail.com',
  'mdashrafulislamashh@gmail.com'
];

const isSuperAdmin = (email) => {
  if (!email) return false;
  const normalizedEmail = email.toLowerCase().trim();
  return SUPER_ADMIN_EMAILS.some(adminEmail => adminEmail.toLowerCase() === normalizedEmail);
};

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    const result = await signInWithGoogle();

    if (result.success) {
      // Check if super admin - always gets admin role
      const isSuper = isSuperAdmin(result.user.email);

      // Get additional user data from localStorage if exists
      const users = JSON.parse(localStorage.getItem('jobSeekers') || '[]');
      const employers = JSON.parse(localStorage.getItem('employers') || '[]');

      const jobSeeker = users.find(u => u.email === result.user.email);
      const employer = employers.find(u => u.email === result.user.email);

      let userData = result.user;

      // Super admin always gets admin role - HIGHEST PRIORITY
      if (isSuper) {
        userData = {
          ...userData,
          role: 'admin'
        };
      }
      // Merge with existing profile data if available
      else if (jobSeeker) {
        userData = {
          ...userData,
          displayName: jobSeeker.name || userData.displayName,
          photoURL: jobSeeker.profileImage || userData.photoURL,
          role: 'jobseeker'
        };
      } else if (employer) {
        userData = {
          ...userData,
          displayName: employer.name || userData.displayName,
          photoURL: employer.profileImage || userData.photoURL,
          role: 'employer'
        };
      }

      // Save user data to context (login function will enforce super admin role)
      login(userData);

      // Redirect based on role
      if (userData.role === 'admin') {
        // Admin users go to admin dashboard
        navigate('/admin/dashboard');
      } else if (!userData.role) {
        // No role assigned - go to role selection
        navigate('/select-role');
      } else {
        // Regular users go to their dashboard
        navigate('/dashboard');
      }
    } else {
      setError(result.error || 'Failed to login with Google');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">💼</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Welcome to JobMatch
          </h2>
          <p className="text-gray-600">
            Sign in with your Google account to continue
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center bg-white border-2 border-blue-500 text-gray-700 py-4 rounded-lg font-semibold hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </button>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-700 text-center">
              <span className="font-semibold text-blue-700">Note:</span> First-time users will be asked to select their role (Job Seeker or Employer) after signing in.
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-gray-600 text-sm">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
